"use client";

/**
 * Inline `[n]` citation marker button.
 *
 * Extracted from the prototype `compass-intake.jsx:77` (the `Cite` component).
 * Replaces `.cite` CSS classes with Tailwind utilities.
 */

export interface CitationChipProps {
  /** Citation number displayed inside the chip. */
  n: number;
  /** Whether this citation is currently selected / active. */
  active?: boolean;
  /** Render as an "empty" placeholder (dimmed, dashed border). */
  empty?: boolean;
  /** Callback fired when the chip is clicked. Receives the citation number. */
  onClick?: (n: number) => void;
}

/**
 * Small bracketed citation marker `[1]`, `[2]`, etc.
 *
 * Used inline within AI-generated text to reference sources.
 * Clicking opens the citation detail in the rail.
 */
export function CitationChip({
  n,
  active = false,
  empty = false,
  onClick,
}: CitationChipProps) {
  return (
    <button
      type="button"
      className={[
        "inline-flex items-center justify-center",
        "min-w-[20px] h-[18px] px-[5px]",
        "font-mono text-[10px] font-medium leading-none",
        "rounded-[3px] border",
        "cursor-pointer select-none",
        "transition-colors duration-150",
        // Empty state: dashed border, muted
        empty
          ? "border-dashed border-(--border-default) text-(--fg-muted) bg-transparent"
          : active
            ? [
                "bg-(--brand-navy) text-(--fg-inverse)",
                "border-(--fg-brand)",
              ].join(" ")
            : [
                "bg-(--bg-canvas) text-(--fg-secondary)",
                "border-(--border-subtle)",
                "hover:border-(--border-default) hover:bg-(--bg-hover)",
              ].join(" "),
      ].join(" ")}
      aria-label={`Citation ${n}`}
      data-active={active || undefined}
      onClick={() => onClick?.(n)}
    >
      {n}
    </button>
  );
}
